import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCircleInfo,
    faCartShopping,
    faPlus,
    faBuilding,
    faToggleOn,
    faCreditCard,
    faCrown,
    faCheckCircle,
    faEye,
} from "@fortawesome/free-solid-svg-icons";

import PaymentGatewayRedirection from "../../../components/clinic/auxiliar/PaymentGatewayRedirection";
import EmptyTable from "../../../components/general/auxiliar/table/EmptyTable";
import LoadingTable from "../../../components/general/auxiliar/table/LoadingTable";
import OverviewTable from "../../../components/general/auxiliar/table/OverviewTable";
import OverviewCardList from "../../../components/general/auxiliar/table/OverviewCard";
import TableBadge from "../../../components/general/auxiliar/table/TableBadge";
import { getAllClinics, getClinicPaymentGatewayParams } from "../../../actions/clinics";
import { dispatchActiveClinicSave } from "../../../store/reducers/activeClinicReducer";
import { handleSubmit, fetchData, getTableState } from "../../../auxiliar/auxFunctions";

const OverviewClinics = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { id: managerId } = useSelector((state) => state.auth);
    const { id: activeClinicId } = useSelector((state) => state.activeClinic);

    const [clinics, setClinics] = useState({ waiting: true });
    const [paymentData, setPaymentData] = useState({ waiting: true });

    useEffect(() => {
        fetchData(dispatch, getAllClinics, { manager_id: managerId }, setClinics);
    }, [managerId]);

    const setActiveClinic = (clinic) => {
        dispatchActiveClinicSave(dispatch, clinic?.clinic_id, clinic?.name, clinic?.status);
    };

    // Table columns configuration
    const columns = [
        {
            label: "Nombre",
            icon: faBuilding,
            width: "w-[30%]",
        },
        {
            label: "Estado",
            icon: faToggleOn,
            width: "w-[15%]",
        },
        {
            label: "Pago",
            icon: faCreditCard,
            cellClass: "hidden sm:table-cell",
            width: "w-[25%]",
        },
        {
            label: "Plan",
            icon: faCrown,
            cellClass: "hidden md:table-cell",
            width: "w-[15%]",
        },
        {
            label: "Activa",
            icon: faCheckCircle,
            width: "w-[15%]",
        },
    ];

    // Function to get row data for table
    const getRowData = (clinic, clinicId) => {
        let payment = "Pagado";
        let infoPayment = "Pago mensual realizado";
        if (!clinic.paid) {
            if (!clinic.active) {
                payment = "Rechazado";
                infoPayment = "Pago mensual rechazado. Realiza el pago para activar la clínica";
            } else {
                payment = "Pendiente";
                infoPayment = "Pago mensual rechazado. Reintentaremos el pago en los próximos días";
            }
        }

        return [
            // Nombre
            <div
                key={`${clinicId}-name`}
                className="font-semibold text-base-content truncate"
                title={clinic.name}
            >
                {clinic.name}
            </div>,
            // Estado
            <TableBadge
                key={`${clinicId}-status`}
                variant={clinic.active ? "success" : "error"}
                size="sm"
            >
                {clinic.active ? "Activa" : "Inactiva"}
            </TableBadge>,
            // Pago
            <div key={`${clinicId}-payment`} className="flex items-center gap-2">
                <span className="text-base-content/70 truncate">{payment}</span>
                <div className="tooltip tooltip-top" data-tip={infoPayment}>
                    <FontAwesomeIcon icon={faCircleInfo} className="text-primary" />
                </div>
                <div className="tooltip tooltip-top" data-tip="Abrir pasarela de pago">
                    <button
                        className="btn btn-xs btn-ghost text-secondary"
                        onClick={handleSubmit(
                            fetchData,
                            dispatch,
                            getClinicPaymentGatewayParams,
                            { clinic_id: clinicId },
                            setPaymentData,
                        )}
                    >
                        <FontAwesomeIcon icon={faCartShopping} />
                    </button>
                </div>
            </div>,
            // Plan
            <span key={`${clinicId}-plan`} className="text-base-content/70">
                {clinic.payment_plan.type === "full" ? "Completo" : "Colegiado"}
            </span>,
            // Activa (checkbox)
            <input
                key={`${clinicId}-active`}
                type="checkbox"
                className="checkbox disabled:bg-disabled disabled:text-disabled-content checkbox-primary hover:cursor-pointer"
                checked={activeClinicId?.toString() === clinicId?.toString()}
                onChange={() => setActiveClinic(clinic)}
            />,
        ];
    };

    // Function to get detail path (for table actions)
    const getDetailPath = (clinicId) => `/clinic/${clinicId}`;

    // Custom actions renderer for table
    const renderActions = (_, clinicId) => (
        <button
            type="button"
            className="btn btn-primary btn-sm disabled:cursor-not-allowed disabled:text-disabled-content disabled:bg-disabled"
            onClick={handleSubmit(navigate, getDetailPath(clinicId))}
            disabled={activeClinicId?.toString() !== clinicId?.toString()}
        >
            <FontAwesomeIcon icon={faEye} className="mr-2" />
            Detalles
        </button>
    );

    // Custom actions renderer for cards
    const renderCardActions = (_, clinicId) => (
        <button
            type="button"
            className="btn btn-primary btn-xs sm:btn-sm text-xs sm:text-sm disabled:cursor-not-allowed disabled:text-disabled-content disabled:bg-disabled"
            onClick={handleSubmit(navigate, getDetailPath(clinicId))}
            disabled={activeClinicId?.toString() !== clinicId?.toString()}
        >
            <FontAwesomeIcon icon={faEye} className="mr-1 sm:mr-2 text-xs sm:text-sm" />
            <span className="sm:hidden">Ver</span>
            <span className="hidden sm:inline">Detalles</span>
        </button>
    );

    // Card render functions
    const renderTitle = (clinic) => clinic.name;

    const renderSubtitle = (clinic) => (
        <div className="flex items-center gap-2 mt-1">
            <TableBadge variant={clinic.active ? "success" : "error"} size="xs">
                {clinic.active ? "Activa" : "Inactiva"}
            </TableBadge>
        </div>
    );

    const renderFooter = (clinic) => {
        let payment = "Pagado";
        let infoPayment = "Pago mensual realizado";
        if (!clinic.paid) {
            if (!clinic.active) {
                payment = "Rechazado";
                infoPayment = "Pago mensual rechazado. Realiza el pago para activar la clínica";
            } else {
                payment = "Pendiente";
                infoPayment = "Pago mensual rechazado. Reintentaremos el pago en los próximos días";
            }
        }

        return (
            <div className="space-y-2 text-xs sm:text-sm">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-base-content/70">
                        <FontAwesomeIcon
                            icon={faCreditCard}
                            className="w-3 h-3 text-base-content/40 flex-shrink-0"
                        />
                        <span>{payment}</span>
                        <div className="tooltip tooltip-top" data-tip={infoPayment}>
                            <FontAwesomeIcon icon={faCircleInfo} className="text-primary" />
                        </div>
                    </div>
                    <div className="tooltip tooltip-left" data-tip="Abrir pasarela de pago">
                        <button
                            className="btn btn-xs btn-ghost text-secondary"
                            onClick={handleSubmit(
                                fetchData,
                                dispatch,
                                getClinicPaymentGatewayParams,
                                { clinic_id: clinic.clinic_id },
                                setPaymentData,
                            )}
                        >
                            <FontAwesomeIcon icon={faCartShopping} />
                        </button>
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-base-content/70">
                        <FontAwesomeIcon
                            icon={faCrown}
                            className="w-3 h-3 text-base-content/40 flex-shrink-0"
                        />
                        <span>
                            {clinic.payment_plan.type === "full" ? "Completo" : "Colegiado"}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-base-content/70 text-xs">Activa</span>
                        <input
                            type="checkbox"
                            className="checkbox checkbox-primary checkbox-xs disabled:bg-disabled disabled:text-disabled-content hover:cursor-pointer"
                            checked={activeClinicId?.toString() === clinic.clinic_id?.toString()}
                            onChange={() => setActiveClinic(clinic)}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const tableState = getTableState(clinics);

    const renderContent = () => {
        switch (tableState) {
            case "loading":
                return <LoadingTable message="Cargando clínicas..." />;

            case "empty":
                return (
                    <EmptyTable
                        icon={faBuilding}
                        title="No hay clínicas registradas"
                        description="Añade la primera clínica para empezar a trabajar con MiFisio Agenda."
                        buttonText="Registrar clínica"
                        onButtonClick={handleSubmit(navigate, "/clinic/register")}
                    />
                );

            case "hasData":
            default:
                return (
                    <>
                        <OverviewCardList
                            data={clinics}
                            navigate={navigate}
                            getDetailPath={getDetailPath}
                            renderTitle={renderTitle}
                            renderSubtitle={renderSubtitle}
                            renderFooter={renderFooter}
                            renderActions={renderCardActions}
                        />
                        <OverviewTable
                            data={clinics}
                            navigate={navigate}
                            columns={columns}
                            getRowData={getRowData}
                            getDetailPath={getDetailPath}
                            renderActions={renderActions}
                            actionsWidth="w-[15%]"
                            noTruncateColumns={[0, 2]}
                        />
                    </>
                );
        }
    };

    return (
        <section className="sm:container p-3 mx-auto mt-5 mb-12">
            <h2 className="sr-only">
                Gestiona tus clínicas de fisioterapia en MiFisio Agenda - Clínicas
            </h2>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                <h3 className="text-2xl text-primary">Clínicas</h3>

                {/* Action button */}
                <button
                    className="btn btn-primary btn-sm w-full sm:w-auto"
                    onClick={handleSubmit(navigate, "/clinic/register")}
                >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Registrar clínica
                </button>
            </div>

            <div className="mt-6 relative">{renderContent()}</div>

            <PaymentGatewayRedirection paymentData={paymentData} />
        </section>
    );
};

export default OverviewClinics;
